import * as Yup from "yup";

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  makeStyles
} from "@material-ui/core";
import { Field, Form, Formik } from 'formik';
import { CheckboxWithLabel as FormikCheckboxWithLabel, TextField as FormikTextField } from 'formik-material-ui';
import { useMutation, useQuery } from "react-query";

import CustomAlert from "../../components/CustomAlert";
import CustomDialog from "../../components/CustomDialog"
import DayJsUtils from "@date-io/dayjs";
import { Autocomplete as FormikAutocomplete } from 'formik-material-ui-lab';
import { KeyboardDatePicker as FormikKeyboardDatePicker } from 'formik-material-ui-pickers';
import { KeyboardDatePicker } from "@material-ui/pickers";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { createDocPerKind } from "../../../services/documentsPerKindService";
import dayjs from "dayjs";
import { fetchApplicationKinds } from "../../../services/applicationKindsService";
import { fetchDocuments } from "../../../services/documentsService";
import localeEl from "dayjs/locale/el";
import localeEn from "dayjs/locale/en";
import useLocale from "../../../hooks/useLocale";
import { useState } from "react";

const useStyles = makeStyles({
  dateContainer: {
    width: "100%"
  }
})

const CreateDialog = ({ open, close, refetch }) => {
  const { data: applKinds } = useQuery('applicationKinds', fetchApplicationKinds);
  const { data: documents } = useQuery('documents', fetchDocuments);
  const [error, setError] = useState('');
  const { t, locale } = useLocale();
  dayjs().locale(locale === "el-GR" ? localeEl : localeEn);
  const classes = useStyles();
  // Mutation for creating data
  const { mutate } = useMutation(createDocPerKind, {
    onSuccess: data => {
      refetch();
      close();
    },
    onError: error => {
      switch (error) {
        default: {
          setError(t('A network error occured. Please try again later'))
        }
      }
    }
  });

  const validationSchema = Yup.object().shape({
    dscr: Yup.string()
      .required(t('Field is required'))
      .max(512, t('Too long')),
    appl_kind_id: Yup.number()
      .nullable()
      .required(t('Field is required')),
    doc_id: Yup.number()
      .nullable()
      .required(t('Field is required')),
    req_flg: Yup.number()
      .nullable()
      .required(t('Field is required')),
    serv_flg: Yup.number()
      .nullable()
      .required(t('Field is required')),
    effective_date: Yup.date().required(t('Field is required')).nullable(),
    termination_date: Yup.date().required(t('Field is required')).nullable(),
  })

  const handleCreateItem = (values) => {
    mutate({ items: JSON.stringify([values]) });
  }

  return (
    <MuiPickersUtilsProvider
      locale={locale === "el-GR" ? localeEl : localeEn}
      utils={DayJsUtils}
    >
      <CustomDialog
        open={open}
        maxWidth='sm'
        onClose={close}
      >
        <DialogTitle id="form-dialog-title">{t('Add new Document per Kind')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Fill in the form below and click on Add to add a new Document per Kind')}
          </DialogContentText>
        </DialogContent>
        <CustomAlert
          open={!!error}
          close={() => { setError('') }}
          message={t('A network error occured, please try again later')}
        />
        <Formik
          initialValues={{
            dscr: '',
            appl_kind_id: null,
            req_flg: 0,
            serv_flg: 0,
            doc_id: null,
            effective_date: null,
            termination_date: null,
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            handleCreateItem(values);
            setSubmitting(false);
          }}
        >
          {({ submitForm, isSubmitting, setFieldValue, touched, errors, values }) => (
            <Form>
              <DialogContent>
                <Field
                  margin='normal'
                  component={FormikTextField}
                  fullWidth
                  name='dscr'
                  label={t('Description')}
                />

                <Field
                  name="appl_kind_id_select"
                  component={FormikAutocomplete}
                  options={applKinds}
                  onChange={(event, newValue) => {
                    setFieldValue('appl_kind_id', newValue?.id || '');
                  }}
                  getOptionLabel={(option) => option.dscr}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='appl_kind_id'
                      error={touched['appl_kind_id'] && !!errors['appl_kind_id']}
                      helperText={touched['appl_kind_id'] && errors['appl_kind_id']}
                      label={t('Application Kind')}
                      variant="outlined"
                      margin='normal'
                    />
                  )}
                />

                <Field
                  name="doc_id_select"
                  component={FormikAutocomplete}
                  options={documents}
                  onChange={(event, newValue) => {
                    setFieldValue('doc_id', newValue?.id || '');
                  }}
                  getOptionLabel={(option) => option.dscr}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='doc_id'
                      error={touched['doc_id'] && !!errors['doc_id']}
                      helperText={touched['doc_id'] && errors['doc_id']}
                      label={t('Document')}
                      variant="outlined"
                      margin='normal'
                    />
                  )}
                />

                <Field
                  component={FormikCheckboxWithLabel}
                  type="checkbox"
                  name="req_flg"
                  onChange={(event, newValue) => {
                    setFieldValue('req_flg', newValue ? 1 : 0);
                  }}
                  Label={{ label: t('Required') }}
                />

                <Field
                  component={FormikCheckboxWithLabel}
                  type="checkbox"
                  name="serv_flg"
                  onChange={(event, newValue) => {
                    setFieldValue('serv_flg', newValue ? 1 : 0);
                  }}
                  Label={{ label: t('Search from Service') }}
                />
                <div className={classes.dateContainer}>
                  <Field component={FormikKeyboardDatePicker}
                    label={t('Effective Date')}
                    format="DD/MM/YYYY"
                    style={{ width: "45%", marginBottom: "20px" }}
                    showTodayButton
                    name="effective_date"
                    todayLabel={t("Today")}
                    cancelLabel={t("Cancel")}
                    onChange={(date) => {
                      setFieldValue('effective_date', dayjs(date).format("MM/DD/YYYY"))
                    }}
                  />
                </div>
                <div className={classes.dateContainer}>
                  <Field component={FormikKeyboardDatePicker}
                    label={t('Termination Date')}
                    format="DD/MM/YYYY"
                    style={{ width: "45%", marginBottom: "20px" }}
                    name="termination_date"
                    minDate={values.effective_date || dayjs()}
                    todayLabel={t("Today")}
                    cancelLabel={t("Cancel")}
                    onChange={(date) => {
                      setFieldValue('termination_date', dayjs(date).format("MM/DD/YYYY"))
                    }}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={submitForm} color="primary">
                  {t('Add')}
                </Button>
                <Button onClick={close} color="primary">
                  {t('Cancel')}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>

      </CustomDialog>
    </MuiPickersUtilsProvider>
  )
}

export default CreateDialog;